// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Pipeline service - function exports for pipeline CRUD operations.
 * Used directly by React Query hooks for better tree-shaking.
 */
import type { NodeApiClient } from '@/app-shell/services'
import type { Pipeline, WorkflowDefinition } from '../types'

export type { Pipeline }

export interface PipelineCreateRequest {
  id: string
  name: string
  description?: string | null
  definition: WorkflowDefinition
}

export interface PipelineCloneRequest {
  new_pipeline_id: string
  new_name?: string | null
}

/** Body for saving an agent's pipeline (full replace — no partial updates). */
export interface AgentPipelineUpsertRequest {
  id: string
  name: string
  description?: string | null
  hidden?: boolean
  definition: WorkflowDefinition
}

/** Query key factory for React Query cache management */
export const pipelineKeys = {
  all: ['pipelines'] as const,
  list: (orgId: string, workspaceId: string) => [...pipelineKeys.all, 'list', orgId, workspaceId] as const,
  agent: (orgId: string, workspaceId: string, agentId: string) =>
    [...pipelineKeys.all, 'agent', orgId, workspaceId, agentId] as const,
}

// Function exports for direct use with React Query
export const listPipelines = async (
  client: NodeApiClient,
  orgId: string,
  workspaceId: string,
): Promise<Record<string, Pipeline>> => {
  const { data } = await client.get<Record<string, Pipeline>>(
    `/org/${orgId}/ws/${workspaceId}/pipelines`,
  )
  return data ?? {}
}

export const getPipeline = async (
  client: NodeApiClient,
  orgId: string,
  workspaceId: string,
  pipelineId: string,
): Promise<Pipeline> => {
  const { data } = await client.get<Pipeline>(
    `/org/${orgId}/ws/${workspaceId}/pipelines/${pipelineId}`,
  )
  return data
}

export const createPipeline = async (
  client: NodeApiClient,
  orgId: string,
  workspaceId: string,
  pipeline: PipelineCreateRequest,
): Promise<Pipeline> => {
  const { data } = await client.post<Pipeline>(
    `/org/${orgId}/ws/${workspaceId}/pipelines`,
    pipeline,
  )
  return data
}

export const updatePipeline = async (
  client: NodeApiClient,
  orgId: string,
  workspaceId: string,
  pipelineId: string,
  updates: Partial<Pipeline>,
): Promise<Pipeline> => {
  const { data } = await client.put<Pipeline>(
    `/org/${orgId}/ws/${workspaceId}/pipelines/${pipelineId}`,
    updates,
  )
  return data
}

export const clonePipeline = async (
  client: NodeApiClient,
  orgId: string,
  workspaceId: string,
  pipelineId: string,
  cloneRequest: PipelineCloneRequest,
): Promise<Pipeline> => {
  const { data } = await client.post<Pipeline>(
    `/org/${orgId}/ws/${workspaceId}/pipelines/${pipelineId}/clone`,
    cloneRequest,
  )
  return data
}

export const deletePipeline = async (
  client: NodeApiClient,
  orgId: string,
  workspaceId: string,
  pipelineId: string,
): Promise<void> => {
  await client.delete(`/org/${orgId}/ws/${workspaceId}/pipelines/${pipelineId}`)
}

// --- Agent-owned pipelines ---

/** Get an agent's pipeline; null when the agent has none yet. */
export const getAgentPipeline = async (
  client: NodeApiClient,
  orgId: string,
  workspaceId: string,
  agentId: string,
): Promise<Pipeline | null> => {
  try {
    const { data } = await client.get<Pipeline>(
      `/org/${orgId}/ws/${workspaceId}/agents/${agentId}/pipeline`,
    )
    return data
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status
    if (status === 404) return null
    throw error
  }
}

/** Create or replace an agent's pipeline (also points the agent at it). */
export const saveAgentPipeline = async (
  client: NodeApiClient,
  orgId: string,
  workspaceId: string,
  agentId: string,
  pipeline: AgentPipelineUpsertRequest,
): Promise<Pipeline> => {
  const { data } = await client.put<Pipeline>(
    `/org/${orgId}/ws/${workspaceId}/agents/${agentId}/pipeline`,
    pipeline,
  )
  return data
}

// --- Pipeline Execution ---

/** Job returned when a pipeline is queued */
export interface QueuedPipelineJob {
  jobId: string
  pipelineId?: string
  pipelineLabel?: string
  documentCount: number
}

/** Request to run pipelines on documents */
export interface RunDocumentRef {
  documentId: string
  groupId?: string
}

export interface RunPipelinesRequest {
  documents: RunDocumentRef[]
  /** Agent owns pipeline selection; callers do not send a redundant pipeline ID. */
  agentId: string
  targetVersion?: number
  /** For version comparison pipelines */
  compareVersions?: { old: number; new: number }
}

/** Response from running pipelines */
export interface RunPipelinesResponse {
  jobs: QueuedPipelineJob[]
}

/**
 * Queue pipeline execution for documents.
 *
 * @param client - API client
 * @param orgId - Organization ID
 * @param workspaceId - Workspace ID
 * @param appKey - Application key (e.g., 'contract-review')
 * @param request - Documents and optional pipeline/version
 * @returns Queued job information
 */
export const runPipelines = async (
  client: NodeApiClient,
  orgId: string,
  workspaceId: string,
  appKey: string,
  request: RunPipelinesRequest,
): Promise<RunPipelinesResponse> => {
  const { data } = await client.post<{ jobs?: Array<{ jobId?: string; pipelineId?: string; pipelineLabel?: string }> }>(
    `/org/${orgId}/ws/${workspaceId}/${appKey}/run`,
    request,
  )

  // Normalize response - filter out jobs without IDs
  const jobs: QueuedPipelineJob[] = (data?.jobs ?? [])
    .filter((job): job is { jobId: string; pipelineId?: string; pipelineLabel?: string } =>
      typeof job?.jobId === 'string' && job.jobId.length > 0
    )
    .map(job => ({
      jobId: job.jobId,
      pipelineId: job.pipelineId,
      pipelineLabel: job.pipelineLabel,
      documentCount: request.documents.length,
    }))

  return { jobs }
}

/**
 * PipelineService class - maintains backward compatibility with Services facade.
 * Prefer using function exports directly with React Query for new code.
 */
export class PipelineService {
  constructor(private readonly client: NodeApiClient) {}

  getPipeline = (orgId: string, workspaceId: string, pipelineId: string) =>
    getPipeline(this.client, orgId, workspaceId, pipelineId)

  createPipeline = (orgId: string, workspaceId: string, pipeline: PipelineCreateRequest) =>
    createPipeline(this.client, orgId, workspaceId, pipeline)

  updatePipeline = (orgId: string, workspaceId: string, pipelineId: string, updates: Partial<Pipeline>) =>
    updatePipeline(this.client, orgId, workspaceId, pipelineId, updates)

  clonePipeline = (orgId: string, workspaceId: string, pipelineId: string, cloneRequest: PipelineCloneRequest) =>
    clonePipeline(this.client, orgId, workspaceId, pipelineId, cloneRequest)

  deletePipeline = (orgId: string, workspaceId: string, pipelineId: string) =>
    deletePipeline(this.client, orgId, workspaceId, pipelineId)
}
